import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import officeTransactionService from '../services/officeTransactionService';

const INITIAL_FILTERS = {
    fromDate: '',
    toDate: '',
    productName: '',
    officeLocation: ''
};

const INITIAL_FORM_DATA = {
    date: new Date().toISOString().split('T')[0],
    productName: '',
    quantity: '',
    amount: '',
    officeLocation: '',
    notes: ''
};

const currency = (value) => {
    const amount = Number(value || 0);
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2
    }).format(amount);
};

const OfficeTransactionPage = () => {
    const [transactions, setTransactions] = useState([]);
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
    const [summary, setSummary] = useState({ totalAmount: 0, totalRecords: 0 });
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [officeLocations, setOfficeLocations] = useState([]);
    const [productNames, setProductNames] = useState([]);

    const fetchTransactions = async (page = pagination.page) => {
        try {
            setLoading(true);
            const response = await officeTransactionService.getTransactions(filters, page, pagination.limit);

            if (response.success) {
                setTransactions(response.data || []);
                setPagination(response.pagination || { page: 1, totalPages: 1, total: 0, limit: 20 });
                setSummary(response.summary || { totalAmount: 0, totalRecords: 0 });
            } else {
                toast.error('Failed to fetch transactions');
            }
        } catch (error) {
            console.error('Fetch transactions error:', error);
            toast.error(error?.response?.data?.error || 'Failed to fetch transactions');
        } finally {
            setLoading(false);
        }
    };

    const fetchDropdownData = async () => {
        try {
            const [locationsResponse, productsResponse] = await Promise.all([
                officeTransactionService.getOfficeLocations(),
                officeTransactionService.getProductNames()
            ]);

            if (locationsResponse.success) {
                setOfficeLocations(locationsResponse.data || []);
            }
            if (productsResponse.success) {
                setProductNames(productsResponse.data || []);
            }
        } catch (error) {
            console.error('Fetch dropdown data error:', error);
        }
    };

    useEffect(() => {
        fetchTransactions(1);
        fetchDropdownData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters(INITIAL_FILTERS);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const submitData = {
                ...formData,
                quantity: Number(formData.quantity),
                amount: Number(formData.amount)
            };

            const response = await officeTransactionService.createTransaction(submitData);

            if (response.success) {
                toast.success('Transaction created successfully');
                setShowForm(false);
                setFormData(INITIAL_FORM_DATA);
                fetchTransactions(pagination.page);
                fetchDropdownData(); // Refresh dropdown data
            } else {
                toast.error(response.error || 'Operation failed');
            }
        } catch (error) {
            console.error('Submit error:', error);
            toast.error(error?.response?.data?.error || 'Operation failed');
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > pagination.totalPages) return;
        fetchTransactions(newPage);
    };

    const serialStart = (pagination.page - 1) * pagination.limit;

    return (
        <MainLayout>
            <div className="max-w-7xl mx-auto p-4 space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-charcoal">Office Transactions</h1>
                        <p className="text-sm text-gray-500 mt-1">Manage office expenses and purchases</p>
                    </div>
                    <button
                        onClick={() => {
                            setFormData(INITIAL_FORM_DATA);
                            setShowForm(true);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Add Transaction
                    </button>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        <input
                            type="date"
                            name="fromDate"
                            value={filters.fromDate}
                            onChange={handleFilterChange}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                            placeholder="From Date"
                        />
                        <input
                            type="date"
                            name="toDate"
                            value={filters.toDate}
                            onChange={handleFilterChange}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                            placeholder="To Date"
                        />
                        <input
                            type="text"
                            name="productName"
                            value={filters.productName}
                            onChange={handleFilterChange}
                            placeholder="Product Name"
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        />
                        <input
                            type="text"
                            name="officeLocation"
                            value={filters.officeLocation}
                            onChange={handleFilterChange}
                            placeholder="Office Location"
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        />
                        <button
                            onClick={clearFilters}
                            className="px-4 py-2 bg-gray-100 text-charcoal rounded-lg text-sm hover:bg-gray-200"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                        <p className="text-xs uppercase tracking-wide text-gray-500">Total Amount</p>
                        <p className="text-2xl mt-2 font-semibold text-charcoal">{currency(summary.totalAmount)}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                        <p className="text-xs uppercase tracking-wide text-gray-500">Total Records</p>
                        <p className="text-2xl mt-2 font-semibold text-charcoal">{summary.totalRecords}</p>
                    </div>
                </div>

                {/* Transactions Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-gray-600">
                                <tr>
                                    <th className="p-3 text-left">S.No</th>
                                    <th className="p-3 text-left">Date</th>
                                    <th className="p-3 text-left">Product Name</th>
                                    <th className="p-3 text-left">Quantity</th>
                                    <th className="p-3 text-left">Amount</th>
                                    <th className="p-3 text-left">Office Location</th>
                                    <th className="p-3 text-left">Created By</th>
                                    <th className="p-3 text-left">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={8} className="p-6 text-center text-gray-500">Loading transactions...</td></tr>
                                ) : transactions.length === 0 ? (
                                    <tr><td colSpan={8} className="p-6 text-center text-gray-500">No transactions found.</td></tr>
                                ) : (
                                    transactions.map((transaction, index) => (
                                        <tr key={transaction._id} className="border-t border-gray-100">
                                            <td className="p-3">{serialStart + index + 1}</td>
                                            <td className="p-3">{new Date(transaction.date).toLocaleDateString()}</td>
                                            <td className="p-3">{transaction.productName}</td>
                                            <td className="p-3">{transaction.quantity}</td>
                                            <td className="p-3 font-semibold">{currency(transaction.amount)}</td>
                                            <td className="p-3">{transaction.officeLocation}</td>
                                            <td className="p-3">{transaction.createdBy?.name || '-'}</td>
                                            <td className="p-3">
                                                <span className="text-gray-500 text-xs">View Only</span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between p-3 border-t border-gray-100">
                        <p className="text-sm text-gray-500">Total: {pagination.total || 0} records</p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page <= 1}
                                className="px-3 py-1 rounded-md border border-gray-300 text-sm disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <span className="text-sm">Page {pagination.page} / {pagination.totalPages}</span>
                            <button
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page >= pagination.totalPages}
                                className="px-3 py-1 rounded-md border border-gray-300 text-sm disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
                        <h2 className="text-xl font-semibold text-charcoal">
                            Add New Transaction
                        </h2>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                            <input
                                type="date"
                                name="date"
                                value={formData.date}
                                onChange={handleFormChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                            <input
                                type="text"
                                name="productName"
                                value={formData.productName}
                                onChange={handleFormChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                placeholder="Enter product name"
                                required
                                maxLength={200}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                                <input
                                    type="number"
                                    name="quantity"
                                    value={formData.quantity}
                                    onChange={handleFormChange}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    placeholder="0"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                                <input
                                    type="number"
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleFormChange}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    placeholder="0.00"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Office Location *</label>
                            <input
                                type="text"
                                name="officeLocation"
                                value={formData.officeLocation}
                                onChange={handleFormChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                placeholder="Enter office location"
                                required
                                maxLength={100}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                            <textarea
                                name="notes"
                                value={formData.notes}
                                onChange={handleFormChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[80px]"
                                placeholder="Optional notes"
                                maxLength={500}
                            />
                        </div>

                        <div className="flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowForm(false);
                                    setFormData(INITIAL_FORM_DATA);
                                }}
                                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Create
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </MainLayout>
    );
};

export default OfficeTransactionPage;